import React from "react";
import { Card, CardBody, CardFooter, Link, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-auth";

import { PageTitle } from "@/components/UI/PageTitle";

interface DataSource {
  name: string;
  icon: string | React.FC;
  path: string;
  description: string;
  enabled?: boolean;
}

const dataSources: DataSource[] = [
  {
    name: "Pathway Index",
    icon: "fluent:folder-people-24-filled",
    path: "/datahub/files",
    description: "Pathway Index Description",
    enabled: true,
  },
  {
    name: "Figma",
    icon: "logos:figma",
    path: "/datahub/figma",
    description: "Perform data driven prototyping and product design",
  },
  {
    name: "GitHub",
    icon: "logos:github-icon",
    path: "/datahub/github",
    description: "Explore insights on all your code and configuration items",
  },
  {
    name: "Google Drive",
    icon: "logos:google-drive",
    path: "/datahub/google-drive",
    description: "Access and sync files from Google Drive",
    enabled: true,
  },
  {
    name: "Jira",
    icon: "logos:jira",
    path: "/datahub/jira",
    description: "Analyse delivery insights across your product teams",
  },
  {
    name: "MCP Server",
    icon: "mdi:server",
    path: "/datahub/mcp",
    description: "MCP Server Description",
  },
];

export default function DataHubIndexPage() {
  const { brand } = useBrand();
  const { t } = useTranslation();

  return (
    <div className="p-4 max-w-full">
      <PageTitle
        centered
        dataId="datahub-index-title"
        icon="solar:database-linear"
        img={`/brands/${brand.prefix || "webuild"}/knowledge.svg`}
        subtitle="Data Hub Subtitle"
        title={brand.menuItems?.["Knowledge Hub"]?.name || "Knowledge Hub"}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {dataSources.map((source, index) => {
          const isEnabled = source.enabled === true;

          const cardContent = (
            <Card
              className={`h-full transition-all duration-300 border border-default-200 ${
                isEnabled ? "group-hover:shadow-lg group-hover:scale-105" : "opacity-50"
              }`}
            >
              <CardBody className="flex flex-col items-center text-center p-6 relative">
                {!isEnabled && (
                  <Chip className="absolute top-2 right-2" color="default" size="sm" variant="flat">
                    {t("Coming Soon")}
                  </Chip>
                )}
                <div className="mb-4">
                  {typeof source.icon === "string" ? (
                    <Icon
                      className={
                        isEnabled ? "text-default-700 dark:text-white" : "text-default-400"
                      }
                      height={48}
                      icon={source.icon}
                      width={48}
                    />
                  ) : (
                    <source.icon />
                  )}
                </div>
                <h3
                  className={`text-lg font-semibold mb-2 transition-colors ${
                    isEnabled
                      ? "text-default-900 dark:text-white group-hover:text-primary"
                      : "text-default-500"
                  }`}
                >
                  {t(source.name)}
                </h3>
              </CardBody>
              <CardFooter className="pt-0 pb-4 px-6">
                <p
                  className={`text-sm text-center ${isEnabled ? "text-default-600 dark:text-default-200" : "text-default-400"}`}
                >
                  {t(source.description)}
                </p>
              </CardFooter>
            </Card>
          );

          if (isEnabled) {
            return (
              <Link
                key={index}
                className="group cursor-pointer text-inherit no-underline"
                href={source.path}
              >
                {cardContent}
              </Link>
            );
          }

          return (
            <div key={index} className="cursor-not-allowed">
              {cardContent}
            </div>
          );
        })}
      </div>
    </div>
  );
}
